import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import {
  ButtonExportCSVAsync,
  Cell,
  DateRangePicker,
  Empty,
  FieldWrapper,
  Filter,
  FilterButton,
  HeaderCell,
  HeaderRow,
  Input,
  PaginationFooter,
  Row,
  Section,
  SectionHeader,
  Select,
  SortLabel,
  Table,
  TableBody,
  TableHead,
} from 'components';
import { DDOption } from 'config/const';
import { toRem } from 'design';
import useDefaultFirstSite from 'hooks/useDefaultFirstSite';
import { getQueryStringValue } from 'hooks/useQueryString';
import ListContext, { ListContextProvider } from 'providers/ListContextProvider';
import { BusinessDayLabel } from 'screens/Payments/styles';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getSitePayoutsRequest, Payouts, selectPayouts, selectTotalCountPayouts } from 'store/Collections/Payouts';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { selectSiteAcceptance } from 'store/Site';
import { getSitePayoutStatsRequest, selectPayoutStatsByDate, selectPayoutStatsNetTotal, selectPayoutStatsStatusCount, Stats } from 'store/Stats';
import { IStore } from 'store/types';
import { ApiQueryOperator, DisbursementMethod, IApiQueryParam, IPayout, PayoutStatus } from 'types';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForApi } from 'utils/date';
import { capitalize } from 'utils/strings';
import * as API from '../../api';
import { PaidPaymentsRow, ReportTableHeader } from './components';
import PaidPaymentsChart from './PaidPaymentsChart';

enum PaymentTypes {
  TIP_ACCEPTANCE = 'acceptance',
  PREFUND = 'prefunded',
}

const ReportSectionHeader = styled(SectionHeader)`
  h3 {
    font-size: ${toRem(24)};
  }

  .subtitle {
    margin-left: ${toRem(8)};
    font-size: ${toRem(14)};
  }
`;
interface IDependenciesType {
  currentSiteIdDep: string;
}

const DEFAULT_END_DATE = dayjs(new Date()).format('MM-DD-YYYY');
const DEFAULT_START_DATE = dayjs(new Date()).subtract(6, 'day').format('MM-DD-YYYY');
const DEFAULT_SORT = 'employee_name';

const ReportsPaidPayments = () => {
  const dispatch = useDispatch();
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.GET_SITE_PAYOUTS_REQUEST));

  useDefaultFirstSite();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteAcceptance = useSelector(selectSiteAcceptance());

  const payouts = useSelector(selectPayouts());
  const payoutsTotal = useSelector(selectTotalCountPayouts());

  const paymentStats = useSelector(selectPayoutStatsByDate());
  const isStatsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_SITE_PAYOUT_STATS_REQUEST));

  const paidTotal = useSelector(selectPayoutStatsNetTotal(PayoutStatus.PAID)) || 0;
  const payrollTotal = useSelector(selectPayoutStatsNetTotal(PayoutStatus.PAYROLL)) || 0;
  const paidCount = useSelector(selectPayoutStatsStatusCount(PayoutStatus.PAID)) || 0;
  const payrollCount = useSelector(selectPayoutStatsStatusCount(PayoutStatus.PAYROLL)) || 0;

  const totalPaidPayments = paidTotal + payrollTotal;
  const totalPaidCount = paidCount + payrollCount;

  const fetchData = (queryParams: IApiQueryOptions) => {
    const { currentSiteIdDep } = queryParams.meta as IDependenciesType;
    const queryParamsWithoutMeta = omit(queryParams, 'meta');

    const startDate = getQueryStringValue(queryParams.q, 'payment_date', ApiQueryOperator.gte) as string;
    const endDate = getQueryStringValue(queryParams.q, 'payment_date', ApiQueryOperator.lte) as string;

    if (currentSiteIdDep && startDate && endDate) {
      const filters = [...(queryParamsWithoutMeta.q || []), { key: 'status', operator: ApiQueryOperator.in, value: [PayoutStatus.PAID, PayoutStatus.PAYROLL] }];
      const filtersWithoutPaymentDate = filters.filter((q) => q.key !== 'payment_date');

      dispatch(getSitePayoutStatsRequest(currentSiteIdDep, startDate, endDate, { q: filtersWithoutPaymentDate }));
      dispatch(getSitePayoutsRequest(currentSiteIdDep, { ...queryParamsWithoutMeta, q: filters }));
    }
  };

  const exportCSV = async (siteId: string, q?: IApiQueryParam[], sort?: string): Promise<IExportCSV> => {
    const response = await API.DataExport.sitePayouts(siteId, { q: [...q, { key: 'status', operator: ApiQueryOperator.in, value: [PayoutStatus.PAID, PayoutStatus.PAYROLL] }], sort, offset: 0, limit: 0 });
    return CSVFormatter.payouts(response.body);
  };

  return currentSite ? (
    <>
      <ListContextProvider
        fetchData={fetchData}
        totalSelector={() => payoutsTotal}
        dependencies={{
          currentSiteIdDep: currentSite.id,
        }}
        defaultOffset={0}
        defaultLimit={10}
        defaultSort={DEFAULT_SORT}
        defaultFilters={[
          {
            key: 'payment_date',
            operator: ApiQueryOperator.gte,
            value: formatDateForApi(DEFAULT_START_DATE),
          },
          {
            key: 'payment_date',
            operator: ApiQueryOperator.lte,
            value: formatDateForApi(DEFAULT_END_DATE),
          },
        ]}
        dataKey="reportsPaidPayments"
      >
        <ListContext.Consumer>
          {({ methods, components, query }) => {
            const { resultsPerPage: ResultsPerPage, pagination: Pager } = components;
            const { onFiltersChange, onSortChange, isSortedBy, isSortDesc } = methods;
            const { q, sort } = query;
            const isDescendingSort = isSortDesc();
            const [nameSorted, paymentDateSorted, grossSorted, holdbackSorted, netSorted, disbursementSorted] = ['employee_name', 'payment_date', 'gross', 'holdback', 'net', 'disbursement_method'].map((header) => isSortedBy(header));
            const debounceMs = 500;
            const onNameChange = (newName: string) => {
              const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'employee_name') as IApiQueryParam[];
              const filters =
                newName.trim() !== ''
                  ? [
                      ...otherFilters,
                      {
                        key: 'employee_name',
                        operator: ApiQueryOperator.like,
                        value: newName,
                      },
                    ]
                  : otherFilters;
              onFiltersChange(filters);
            };
            const onPaymentIdChange = (newId: string) => {
              const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'id') as IApiQueryParam[];
              const filters =
                newId.trim() !== ''
                  ? [
                      ...otherFilters,
                      {
                        key: 'id',
                        operator: ApiQueryOperator.like,
                        value: newId,
                      },
                    ]
                  : otherFilters;
              onFiltersChange(filters);
            };

            const onDisburmentMethodChange = (newVal: any[]) => {
              const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'disbursement_method') as IApiQueryParam[];
              const filters =
                newVal.length > 0
                  ? [
                      ...otherFilters,
                      {
                        key: 'disbursement_method',
                        operator: ApiQueryOperator.in,
                        value: newVal.map(({ value }: DDOption) => value).join(','),
                      },
                    ]
                  : otherFilters;
              onFiltersChange(filters);
            };

            const onPaymentTypeChange = (newVal: any[]) => {
              const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'fund_account_type') as IApiQueryParam[];
              const ptValue = newVal.map(({ value }: DDOption) => value);

              // add null and prefund to handle legacy fund_account_type
              if (ptValue.includes(PaymentTypes.PREFUND)) {
                ptValue.push(null);
                ptValue.push('prefund');
              }

              const filters =
                ptValue.length > 0
                  ? [
                      ...otherFilters,
                      {
                        key: 'fund_account_type',
                        operator: ApiQueryOperator.in,
                        value: ptValue.join(','),
                      },
                    ]
                  : otherFilters;
              onFiltersChange(filters);
            };

            const debouncedOnPaymentIdChange = debounce(onPaymentIdChange, debounceMs);
            const debouncedOnNameChange = debounce(onNameChange, debounceMs);
            const debouncedOnDisburmentMethodChange = debounce(onDisburmentMethodChange, debounceMs);
            const debouncedOnPaymentTypeChange = debounce(onPaymentTypeChange, debounceMs);

            const setDates = ([start, end]: string[]) => {
              const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'payment_date') as IApiQueryParam[];
              const isValid = dayjs(start, 'YYYY-MM-DD', true).isValid() && dayjs(end, 'YYYY-MM-DD', true).isValid();
              if (isValid) {
                (() => {
                  const filters = [
                    ...otherFilters,
                    {
                      key: 'payment_date',
                      operator: ApiQueryOperator.gte,
                      value: start,
                    },
                    {
                      key: 'payment_date',
                      operator: ApiQueryOperator.lte,
                      value: end,
                    },
                  ];
                  onFiltersChange(filters, true);
                })();
              }
            };
            const getDisplayFilters = (q: IApiQueryParam[] = []) => q.filter((item) => !['payment_date', 'status'].includes(item.key));
            const startDateRange = getQueryStringValue(q, 'payment_date', ApiQueryOperator.gte) as string;
            const endDateRange = getQueryStringValue(q, 'payment_date', ApiQueryOperator.lte) as string;

            return (
              <Section data-cy="table">
                <ReportSectionHeader title={centsToDollarsDisplay(totalPaidPayments)} value={`(${totalPaidCount} payments)`} isLoading={isStatsLoading}>
                  <BusinessDayLabel>{isLoading ? <Skeleton width={90} /> : 'Business Days'}</BusinessDayLabel>
                  <DateRangePicker fluidWidth={false} inputHeight="small" onSubmit={setDates} pickerPosition="right" startDate={DEFAULT_START_DATE} endDate={DEFAULT_END_DATE} submitLabel="apply" />
                </ReportSectionHeader>

                <PaidPaymentsChart paymentStats={paymentStats} isLoading={isStatsLoading} />

                <ReportTableHeader
                  title="Results"
                  count={payoutsTotal || 0}
                  actions={
                    <>
                      <FilterButton filterActive={filterActive} appliedFilters={getDisplayFilters(q).length} onClick={() => setFilterActive(!filterActive)} />
                      <ButtonExportCSVAsync disabled={!payoutsTotal} exportFunc={() => exportCSV(currentSite.id, q, sort)} fileName={`PaidPayments_${currentSite.name}_${startDateRange}_${endDateRange}`} />
                    </>
                  }
                  isLoading={isLoading}
                />

                <Filter isVisible={filterActive}>
                  <FieldWrapper label="Name" data-cy="filter-name">
                    <Input placeholder="Name" onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnNameChange(e.currentTarget.value)} />
                  </FieldWrapper>
                  <FieldWrapper label="Payment ID" data-cy="filter-payment">
                    <Input placeholder="Payment ID" onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnPaymentIdChange(e.currentTarget.value)} />
                  </FieldWrapper>
                  <FieldWrapper label="Disbursement">
                    <Select
                      options={[
                        {
                          label: capitalize(DisbursementMethod.INSTANT),
                          value: DisbursementMethod.INSTANT,
                        },
                        {
                          label: capitalize(DisbursementMethod.PAYROLL),
                          value: DisbursementMethod.PAYROLL,
                        },
                      ]}
                      values={[]}
                      multi
                      onChange={(selected) => debouncedOnDisburmentMethodChange(selected)}
                      data-cy="disbursement-method-select"
                    />
                  </FieldWrapper>
                  {currentSiteAcceptance?.enabled && (
                    <FieldWrapper label="Payment Type">
                      <Select
                        options={[
                          {
                            label: capitalize(PaymentTypes.TIP_ACCEPTANCE),
                            value: PaymentTypes.TIP_ACCEPTANCE,
                          },
                          {
                            label: capitalize(PaymentTypes.PREFUND),
                            value: PaymentTypes.PREFUND,
                          },
                        ]}
                        values={[]}
                        multi
                        onChange={(selected) => debouncedOnPaymentTypeChange(selected)}
                        data-cy="payment-type-select"
                      />
                    </FieldWrapper>
                  )}
                </Filter>
                <Table sticky scrollable>
                  <TableHead>
                    <HeaderRow sticky>
                      <HeaderCell>
                        <SortLabel data-cy="header-employee-name" label="Name" isSorted={nameSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('employee_name')} isLoading={isLoading} />
                      </HeaderCell>

                      <HeaderCell>{!isLoading && 'Payment ID'}</HeaderCell>

                      <HeaderCell>
                        <SortLabel data-cy="header-payment-date" label="Business Day" isSorted={paymentDateSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('payment_date')} isLoading={isLoading} />
                      </HeaderCell>

                      <HeaderCell align="right">
                        <SortLabel data-cy="gross-header" label="Gross" isSorted={grossSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('gross')} isLoading={isLoading} />
                      </HeaderCell>

                      <HeaderCell align="right">
                        <SortLabel data-cy="holdback-header" label="Holdback" isSorted={holdbackSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('holdback')} isLoading={isLoading} />
                      </HeaderCell>

                      <HeaderCell align="right">
                        <SortLabel data-cy="net-header" label="Net" isSorted={netSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('net')} isLoading={isLoading} />
                      </HeaderCell>

                      <HeaderCell>
                        <SortLabel data-cy="disbursement-header" label="Disbursement" isSorted={disbursementSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('disbursement_method')} isLoading={isLoading} />
                      </HeaderCell>
                    </HeaderRow>
                  </TableHead>

                  <TableBody>
                    {!payouts && isLoading && [...Array(query.limit)].map((e, i) => <PaidPaymentsRow key={i} isLoading />)}
                    {payouts?.length === 0 ? (
                      <Row>
                        <Cell colSpan={8}>
                          <Empty container containerHeight={300} icon="Payment" message="No results. Try a different date range." />
                        </Cell>
                      </Row>
                    ) : (
                      payouts?.map((payout: IPayout) => <PaidPaymentsRow key={payout.id} data-cy="paid-payments-row" data-value={payout.id} payout={payout} isLoading={isLoading} />)
                    )}
                  </TableBody>
                </Table>
                <PaginationFooter>
                  {!isLoading && !!payoutsTotal && payoutsTotal > query.limit && ResultsPerPage}
                  {!isLoading && !!payoutsTotal && payoutsTotal > query.limit && Pager}
                </PaginationFooter>
              </Section>
            );
          }}
        </ListContext.Consumer>
      </ListContextProvider>
    </>
  ) : null;
};

export default ReportsPaidPayments;
